import "../../components/common/Tables/DataTable.css"
import "./SpaReport.css"
import React, {useEffect, useState} from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import RootState from "../../redux/states/root.state";
import { AppDispatch } from "../../redux/store";
import { fetchSpa } from "../../redux/actions/spa.actions";
import Header from "../../components/common/Header/Header";
import ReportsGraph from "./components/ReportsGraph";
import ReportsTable from "./components/ReportsTable";
import V1SpaBoyGraph, { V1_SPABOY_GRAPH_FIELDS } from "./components/V1SpaBoyGraph";
import NotFound from "../General/NotFound";
import {commandHistoryOptions, connectionOptions, errorOptions, fieldHistoryOptions, firmwareOptions, liveOptions, RFIDOptions, settingsOptions, spaBoyOptions, spaSetOptions, statusOptions, upgradeRequestHistoryOptions, v1ConnectionOptions, v1ErrorOptions, v1LiveOptions, isV1Yoctub, V1_LIVE_FIELDS, V1_SPABOY_TABLE_COLUMNS } from "./options";
import { palName } from "../../utils/brand";
import ReportUpgradeHistory from "./components/ReportUpgradeHistory";
import SpaCommandsTable from "./components/SpaCommandsTable";
import {TextField, Select, MenuItem, FormControl, InputLabel, Tooltip} from "@mui/material";
import { ChartIcon, ChipIcon, ColumnsIcon, DownloadLineIcon, ExternalLinkIcon, InfoIcon } from "../../components/common/Icons/Icons";
import FieldHistoryTable from "./components/FieldHistoryTable";
import useMQTT from "../../hooks/useMQTT";
import { isV1Spa } from "../../hooks/useSpaCommand";
import {resetLiveData} from "../../redux/reducers/live.reducer";
import {resetSettingsData} from "../../redux/reducers/settings.reducer";
import {ReportTimeZoneProvider, formatInZone, isValidTimeZone, zoneAbbr} from "./reportTime";


// True only when `selected` is exactly `fields` (same members, same count). Used to
// decide if a live report is a SpaBoy report: adding or removing any field makes it a
// plain live report rather than a SpaBoy one.
const isExactly = (selected: any[], fields: string[]) =>
  selected.length === fields.length && fields.every(f => selected.includes(f));

const SpaReport: React.FC = () => {
  const dispatch: AppDispatch = useDispatch();
  const navigate = useNavigate();
  const {id} = useParams();
  var last_week = new Date();
  last_week.setDate(last_week.getDate() - 7)

  const {data: spa, error} = useSelector((state: RootState) => state.spa);

  const {loading, partialLoading, data} = useSelector((state: RootState) => state.spaHistory);
  const [searchParams, setSearchParams] = useSearchParams();

  const [selection, setSelection] = useState<any[]>([]);
  const [category, setCategory] = useState("");
  const [startDate, setStart] = useState<any>(null);
  const [endDate, setEnd] = useState<any>(null);

  const [dataToShow, setShow] = useState("");
  const [selected, setSelected] = useState<any[]>([]);

  const [startInputDate, setStartInputDate] = useState<any>(last_week.getTime());
  const [endInputDate, setEndInputDate] = useState<any>(new Date().getTime());

  const [selectedDate, setSelectedDate] = useState("");
  const [selectedDateRange, setSelectedDateRange] = useState("last_week");

  const [reportOptions, setReportOptions] = useState<any[]>(liveOptions);

  const [viewAsTable, setViewAsTable] = useState(false);

  // Timestamps default to the spa's own zone (from its MQTT settings), falling back to
  // GMT until/unless the spa reports one; "local" switches to the viewer's browser zone.
  const [timeMode, setTimeMode] = useState<'spa' | 'local'>('spa');
  const {data: liveData} = useSelector((state: RootState) => state.live);
  const {data: settingsData} = useSelector((state: RootState) => state.settings);
  // V1 spas never report to AWS IoT: their history comes from the legacy database, so
  // the page offers only the reports and fields it records.
  const isThisSpa = !!id && typeof spa?.id === 'string' && spa.id.toLowerCase() === id.toLowerCase();
  const isV1 = isThisSpa && isV1Spa(spa);
  useMQTT(isThisSpa && !isV1 ? id : "");
  const liveFieldOptions = isV1 ? v1LiveOptions : liveOptions;
  const v1Only = (fields: string[]) => isV1 ? fields.filter(f => V1_LIVE_FIELDS.includes(f)) : fields;

  useEffect(() => {
    if (id){
      dispatch(fetchSpa(id));
    }
  }, [dispatch, id]);

  // Settings left over from another spa's page would report the wrong zone.
  useEffect(() => {
    if (liveData?.spa_guid && liveData.spa_guid !== id) {
      dispatch(resetLiveData());
      dispatch(resetSettingsData());
    }
  }, [dispatch, id, liveData]);

  const spaTimeZone = isValidTimeZone(settingsData?.timezone) ? settingsData.timezone : undefined;
  const reportTimeZone = timeMode === 'local' ? undefined : (spaTimeZone ?? 'UTC');

  const handleGenerate = () => {
    searchParams.delete('page');
    setSearchParams(searchParams);
    setSelected(selection);
    setStart(startInputDate);
    setEnd(endInputDate);
    setShow(category);
    setViewAsTable(false);
  }

  // SpaBoy graph plots these 7 fields; the table additionally shows the diagnostic
  // fields appended below. They run as two separate reports/views.
  // V1's graph is the classic Spa Boy chart instead (V1SpaBoyGraph).
  const spaboyGraphFields = isV1 ? V1_SPABOY_GRAPH_FIELDS : [`sbI1`,`sbI2`,`sbVout`,`sbpH`,`sbORP`,`SBConnected`,`P1`];
  // V1's table is the classic Spa Boy table instead, column for column.
  const spaboyTableFields = isV1
    ? V1_SPABOY_TABLE_COLUMNS.map(c => c.key).filter(k => k !== 'timestamp')
    : [`sbI1`,`sbI2`,`sbVout`,`sbpH`,`sbORP`,`SBConnected`,`P1`, `phSM`,`sbProducing`,`sbVin`,`sbE1p`,`sbE1n`,`sbE2p`,`sbE2n`];

  const handleSpaboyGraph = () => {
    setSelected(spaboyGraphFields);
    setCategory('live');
    setReportOptions(liveFieldOptions);
    setSelection(spaboyGraphFields);
    setStart(startInputDate);
    setEnd(endInputDate);
    setShow('live');
    setViewAsTable(false);
  };

  const handleSpaboyTable = () => {
    setSelected(spaboyTableFields);
    setCategory('live');
    setReportOptions(liveFieldOptions);
    setSelection(spaboyTableFields);
    setStart(startInputDate);
    setEnd(endInputDate);
    setShow('live');
    setViewAsTable(true);
  };

  const handleHeat = () => {
    const heatFields = v1Only([`STemp`,`TSP`,`HTemp`,`H1`]);
    setSelected(heatFields);
    // Set category to 'live' and update selection to show selected options
    setCategory('live');
    setReportOptions(liveFieldOptions);
    setSelection(heatFields);
    setStart(startInputDate);
    setEnd(endInputDate);
    setShow('live');
    setViewAsTable(false);
  };

  const handleDownloadCSV = () => {
    if (!data || data.length === 0) return;
    if (partialLoading) return; // Prevent download while partial loading is in progress

    // Sort data by timestamp to ensure chronological order (oldest to newest)
    // This is important when data is loaded in batches/partial loads
    const sortedData = [...data].sort((a: any, b: any) => {
      const timeA = a.timestamp ? new Date(a.timestamp).getTime() : 0;
      const timeB = b.timestamp ? new Date(b.timestamp).getTime() : 0;
      return timeA - timeB; // Ascending order (oldest first)
    });

    // Convert data to CSV format
    const headers = Object.keys(sortedData[0]).filter(header => header !== 'spa_guid');
    const csvContent = [
      headers.join(','),
      ...sortedData.map((row: Record<string, any>) => 
        headers.map(header => {
          let value = row[header];
          
          // Convert timestamp to the zone the report is being viewed in
          if (header === 'timestamp' && value) {
            value = formatInZone(value, reportTimeZone);
          }
          
          // Handle values that need quotes (strings with commas, quotes, or newlines)
          if (typeof value === 'string' && (value.includes(',') || value.includes('"') || value.includes('\n'))) {
            return `"${value.replace(/"/g, '""')}"`;
          }
          return value;
        }).join(',')
      )
    ].join('\n');

    // Create and download the CSV file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `spa_report_${spa?.nick_name || spa?.id}_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleSelectAll = () => {
    // If all items are selected, deselect all
    if (selection.length === reportOptions.length && reportOptions.length > 0) {
      setSelection([]);
    } else {
      // Otherwise, select all items
      setSelection(reportOptions.map((opt: any) => opt.id));
    }
  };

  const handleChange = (val:any) =>{
    setCategory(val);
    switch(val){
      case 'live':
        setReportOptions(liveFieldOptions);
        break;
      case 'settings':
        setReportOptions(settingsOptions);
        break;
      case 'spa_settings':
        setReportOptions(spaSetOptions);
        break;
      case 'rfid':
        setReportOptions(RFIDOptions);
        break;
      case 'spaboy':
        setReportOptions(spaBoyOptions);
        break;
      case 'firmware':
        setReportOptions(firmwareOptions);
        break;
      case 'connections':
        setReportOptions(isV1 ? v1ConnectionOptions : connectionOptions);
        break;
      case 'errors':
        setReportOptions(isV1 ? v1ErrorOptions : errorOptions);
        break;
      case 'statuses':
        setReportOptions(statusOptions);
        break;
      case 'field_history':
        setReportOptions(fieldHistoryOptions);
        break;  
      case 'command_history':
        setReportOptions(commandHistoryOptions);
        break;
      case 'upgrade_requests':
        setReportOptions(upgradeRequestHistoryOptions);
        break;
    }
    setSelection([]);
  }

  const handleCheck = (opt:any)=>{
    let newArray = [...selection];
    let found = newArray.find((s:any)=>s===opt.id)!==undefined;
    if (found) {
      newArray = selection.filter((s:any)=>
        s!==opt.id
      )
    } else {
      newArray.push(opt.id);
    }

    setSelection(newArray);
  }

  const handleDateChange = (e:any) => {
    let val = e.target.value;
    setSelectedDateRange(val);
    var date = new Date();
    switch(val){
      case "last_week":
        date.setDate(date.getDate() - 7);
        break;

      case "three":
        date.setTime(date.getTime() - 10800000)
        break;

      case "six":
        date.setTime(date.getTime() - 21600000)
        break;

      case "today":
        date.setHours(0,0,0,0)
        break;

      case "last_month":
        date.setMonth(date.getMonth()-1);
        break;

      case "six_months":
        date.setMonth(date.getMonth()-6);
        break;

      case "last_year":
        date.setFullYear(date.getFullYear() - 1);
        break;
    }
    setStartInputDate(date.getTime());
    setEndInputDate(new Date().getTime());
    setSelectedDate(convertTimestampToDate(new Date().getTime()));
  }

  useEffect(() => {
    if (category==='command_history'){
      setSelection(['commandHistory']);
    } else if (category==='upgrade_requests'){
      setSelection(['upgradeRequests']);
    }
  }, [category]);

  const convertTimestampToDate = (timestamp: number) => {
    return new Date(timestamp).toISOString().split("T")[0];
  }

  if (error){
    return <NotFound/>
  }

  // Graph <-> table for live reports. An exact Spa Boy set swaps to its own other half:
  // the table adds the diagnostic fields, the graph drops them again.
  const showTable = () => {
    if (isExactly(selected, spaboyGraphFields)) {
      setSelected(spaboyTableFields);
      setSelection(spaboyTableFields); // Update checkboxes to match
    }
    setViewAsTable(true);
  };

  const showGraph = () => {
    if (isExactly(selected, spaboyTableFields)) {
      setSelected(spaboyGraphFields);
      setSelection(spaboyGraphFields); // Update checkboxes to match
    }
    setViewAsTable(false);
  };

  // Command and upgrade-request history are one fixed field each
  const fixedFields = category==="command_history" || category==="upgrade_requests";
  const allSelected = selection.length === reportOptions.length && reportOptions.length > 0;
  const someSelected = selection.length > 0 && !allSelected;

  const oneMonthAgo = (() => {
    const d = new Date();
    d.setMonth(d.getMonth() - 1);
    return d.getTime();
  })();

  const dateField = (label: string, value: number, setValue: (v: number) => void, min: number, max?: number) => (
    <TextField
      type="date"
      label={label}
      value={convertTimestampToDate(value)}
      onChange={(e)=>{
        const [year, month, day] = e.target.value.split('-').map(Number);
        const localMidnight = new Date(year, month - 1, day);
        setValue(localMidnight.getTime());
      }}
      inputProps={{
        min: convertTimestampToDate(min),
        ...(max !== undefined ? {max: convertTimestampToDate(max)} : {}),
      }}
      InputLabelProps={{shrink: true}}
      size="small"
      fullWidth
    />
  );

  const timeTip = timeMode === 'local'
    ? `Times shown in your local time (${zoneAbbr()})`
    : spaTimeZone
      ? `Times shown in the spa's time zone (${spaTimeZone})`
      : `Spa time zone unknown — times shown in GMT`;

  return (
    <div className="page lp-page">
      <Header
        header="Spa Report"
        headerExtra={spa?.id &&
          <a
            href={`/spa/${spa?.id}`}
            className="lp-spa-link"
            onClick={(e) => {
              if (e.metaKey || e.ctrlKey || e.shiftKey) return;
              e.preventDefault();
              navigate(`/spa/${spa?.id}`);
            }}
          >
            {spa?.nick_name ?? `n/a`}<ExternalLinkIcon/>
          </a>
        }
      />
      <main className="lp-main">
        <div className="lp-layout">
          <aside className="lp-filters rp-builder" aria-label="Report">
            <div className="lp-filters-body">
              {/* One click: a ready-made report over the chosen dates */}
              <div className="lp-group">
                <div className="rp-presets">
                  <button type="button" className="btn btn-sm rp-preset" onClick={handleSpaboyGraph}>{palName} Report</button>
                  <button type="button" className="btn btn-sm rp-preset" onClick={handleSpaboyTable}>{palName} Table</button>
                  <button type="button" className="btn btn-sm rp-preset" onClick={handleHeat}>Heat Report</button>
                </div>
              </div>

              <div className="lp-group">
                <FormControl fullWidth size="small">
                  <InputLabel id="date-range-label">Date Range</InputLabel>
                  <Select
                    labelId="date-range-label"
                    label="Date Range"
                    value={selectedDateRange}
                    onChange={(e)=>handleDateChange(e)}
                  >
                    <MenuItem value="three">Last 3 Hours</MenuItem>
                    <MenuItem value="six">Last 6 Hours</MenuItem>
                    <MenuItem value="today">Today</MenuItem>
                    <MenuItem value="last_week">Last Week</MenuItem>
                    <MenuItem value="last_month">Last Month</MenuItem>
                  </Select>
                </FormControl>
                <div className="lp-range">
                  {dateField("From", startInputDate, setStartInputDate, oneMonthAgo, endInputDate)}
                  {dateField("Until", endInputDate, setEndInputDate, Math.max(startInputDate, oneMonthAgo))}
                </div>
              </div>

              <div className="lp-group rp-fields-group">
                <FormControl fullWidth size="small">
                  <InputLabel id="category-label">Category</InputLabel>
                  <Select
                    labelId="category-label"
                    label="Category"
                    value={category}
                    onChange={(e)=>handleChange(e.target.value)}
                  >
                    <MenuItem value="live">Live</MenuItem>
                    <MenuItem value="settings">Settings</MenuItem>
                    <MenuItem value="spa_settings">Spa Settings</MenuItem>
                    {!isV1 && <MenuItem value="rfid">RFID</MenuItem>}
                    <MenuItem value="spaboy">{palName}</MenuItem>
                    <MenuItem value="firmware">Firmware</MenuItem>
                    <MenuItem value="connections">Connections</MenuItem>
                    {(!isV1 || isV1Yoctub(spa)) && <MenuItem value="errors">Errors</MenuItem>}
                    {!isV1 && <MenuItem value="statuses">Statuses</MenuItem>}
                    {!isV1 && <MenuItem value="field_history">Field History</MenuItem>}
                    {!isV1 && <MenuItem value="command_history">Command History</MenuItem>}
                    {!isV1 && <MenuItem value="upgrade_requests">Upgrade Request History</MenuItem>}
                  </Select>
                </FormControl>

                {category!=='' && (
                  <div className="rp-fields" role="group" aria-label="Fields">
                    {!fixedFields &&
                      <label className="rp-field rp-field-all">
                        <input
                          type="checkbox"
                          className="dt-check"
                          checked={allSelected}
                          ref={(el)=>{ if (el) el.indeterminate = someSelected; }}
                          onChange={handleSelectAll}
                        />
                        All
                        <span className="rp-field-count">{selection.length}/{reportOptions.length}</span>
                      </label>}
                    {reportOptions.map((opt: any) => (
                      <label key={opt.id} className={`rp-field ${fixedFields ? 'disabled' : ''}`}>
                        <input
                          type="checkbox"
                          className="dt-check"
                          checked={selection.some(c => c === opt.id)}
                          onChange={()=>handleCheck(opt)}
                          disabled={fixedFields}
                        />
                        {opt?.label}
                      </label>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="lp-filters-foot">
              <button type="button" className="btn btn-sm btn-primary" onClick={handleGenerate} disabled={category==='' || selection.length===0}>
                Generate Report
              </button>
            </div>
          </aside>

          <section className="dt rp-result" aria-label="Report results">
            <div className="dt-toolbar">
              <div className="dt-toolbar-start">
                {dataToShow==="live" &&
                  <div className="segmented rp-segmented" role="group" aria-label="View as">
                    <button type="button" className={!viewAsTable ? 'active' : ''} aria-pressed={!viewAsTable} onClick={showGraph}>
                      <ChartIcon/> Graph
                    </button>
                    <button type="button" className={viewAsTable ? 'active' : ''} aria-pressed={viewAsTable} onClick={showTable}>
                      <ColumnsIcon/> Table
                    </button>
                  </div>}

                <Tooltip arrow title={timeTip}>
                  <div className="segmented rp-segmented" role="group" aria-label="Timestamp time zone">
                    <button type="button" className={timeMode==='spa' ? 'active' : ''} aria-pressed={timeMode==='spa'} onClick={()=>setTimeMode('spa')}>
                      {spaTimeZone ? `Spa Time (${zoneAbbr(spaTimeZone)})` : 'GMT'}
                    </button>
                    <button type="button" className={timeMode==='local' ? 'active' : ''} aria-pressed={timeMode==='local'} onClick={()=>setTimeMode('local')}>
                      My Time ({zoneAbbr()})
                    </button>
                  </div>
                </Tooltip>

                <button
                  type="button"
                  className="btn btn-sm dt-tool-btn lp-primary-action"
                  onClick={handleDownloadCSV}
                  disabled={!data || data.length === 0 || partialLoading}
                >
                  <DownloadLineIcon/> Download CSV
                </button>

                {dataToShow === "upgrade_requests" &&
                  <button type="button" className="btn btn-sm dt-tool-btn lp-primary-action" onClick={() => navigate('/upgrade-requests')}>
                    <ChipIcon/> View All Requests
                  </button>}

                {dataToShow !== "" &&
                  <span className="rp-note"><InfoIcon/> Data may be up to 5 minutes behind real-time.</span>}
              </div>
            </div>

            <ReportTimeZoneProvider timeZone={reportTimeZone}>
            {
              dataToShow==="live" && !viewAsTable && isV1 && isExactly(selected, spaboyGraphFields) ?
                <V1SpaBoyGraph startDate={startDate} endDate={endDate} spa_guid={spa?.id}/>
              :
              dataToShow==="live" && !viewAsTable ?
                <ReportsGraph selected={selected} startDate={startDate} endDate={endDate} spa_guid={spa?.id}/>
              :
              dataToShow==="upgrade_requests" ?
                <ReportUpgradeHistory spa_guid={spa?.id} startDate={startDate} endDate={endDate}/>
              :
              dataToShow==="command_history" ?
                <SpaCommandsTable spa_guid={spa?.id} startDate={startDate} endDate={endDate}/>
              :
              dataToShow==="field_history" ?
                <FieldHistoryTable spa_guid={spa?.id} startDate={startDate} endDate={endDate} selected={selected}/>
              :
              dataToShow==="" ?
                <div className="dt-state">
                  <div className="dt-empty-title">No report yet</div>
                  <div className="rp-empty-text">Pick a quick report, or choose a category and fields and press Generate Report.</div>
                </div>
              :
                <ReportsTable
                  selected={selected} startDate={startDate} endDate={endDate} spa_guid={spa?.id} category={dataToShow}
                  columns={isV1 && dataToShow === 'live' && isExactly(selected, spaboyTableFields) ? V1_SPABOY_TABLE_COLUMNS : undefined}
                />
            }
            </ReportTimeZoneProvider>
          </section>
        </div>
      </main>
    </div>
  );
};

export default SpaReport;
